import { ReactNode, createContext, useCallback, useContext, useMemo } from "react";

import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { PublicKey } from "@solana/web3.js";

import { useLazyTokenAccountsByOwnerQuery, useTokenAccountsByOwnerQuery, UserTokenAccount } from "../reducers";
import { useWeb3User } from "./context";

export interface UserTokensState {
    tokens?: UserTokenAccount[];
    isLoading: boolean;
    refresh: () => Promise<void>;
    walletAddress?: string;
    error?: Error | null;
}

const UserTokensContext = createContext<UserTokensState | undefined>(undefined);

interface UserTokensProviderProps {
    children: ReactNode;
    walletAddress?: string | null;
}

const FALLBACK_ERROR_MESSAGE = "Failed to load user tokens";

function normalizeQueryError(error?: FetchBaseQueryError | SerializedError): Error | null {
    if (!error) return null;

    if ("status" in error) {
        if ("error" in error) {
            const fetchError = error.error;
            if (typeof fetchError === "string" && fetchError.length > 0) {
                return new Error(fetchError);
            }
            if (fetchError && typeof fetchError === "object") {
                try {
                    return new Error(JSON.stringify(fetchError));
                } catch {
                    return new Error(FALLBACK_ERROR_MESSAGE);
                }
            }
        }

        if ("data" in error && error.data) {
            if (typeof error.data === "string") {
                return new Error(error.data);
            }
            try {
                return new Error(JSON.stringify(error.data));
            } catch {
                return new Error(FALLBACK_ERROR_MESSAGE);
            }
        }

        return new Error(FALLBACK_ERROR_MESSAGE);
    }

    if (error.message) {
        return new Error(error.message);
    }

    return new Error(FALLBACK_ERROR_MESSAGE);
}

export const UserTokensProvider = ({ children, walletAddress: providedWalletAddress }: UserTokensProviderProps) => {
    const { wallet } = useWeb3User();

    const normalizedWalletAddress =
        providedWalletAddress && providedWalletAddress.length > 0 ? providedWalletAddress : undefined;
    const hasProvidedWalletAddress = normalizedWalletAddress !== undefined;

    const { providedPublicKey, providedPublicKeyError } = useMemo(() => {
        if (!normalizedWalletAddress) {
            return { providedPublicKey: null as PublicKey | null, providedPublicKeyError: null as Error | null };
        }

        try {
            return {
                providedPublicKey: new PublicKey(normalizedWalletAddress),
                providedPublicKeyError: null
            };
        } catch (error: unknown) {
            const normalizedError = error instanceof Error ? error : new Error(String(error));
            return {
                providedPublicKey: null,
                providedPublicKeyError: normalizedError
            };
        }
    }, [normalizedWalletAddress]);

    const walletPublicKey = providedPublicKey ?? wallet?.publicKey ?? null;
    const walletAddress = walletPublicKey?.toBase58();

    const skipTokenQuery = !walletAddress;

    const {
        data: tokenAccounts,
        error: queryError,
        isFetching,
        isLoading: isInitialLoading
    } = useTokenAccountsByOwnerQuery(walletAddress ?? "", { skip: skipTokenQuery });

    const [triggerTokenAccountsQuery] = useLazyTokenAccountsByOwnerQuery();

    const refresh = useCallback(async () => {
        if (skipTokenQuery || !walletAddress) return;

        try {
            await triggerTokenAccountsQuery(walletAddress, false).unwrap();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to refresh token accounts", error);
        }
    }, [skipTokenQuery, triggerTokenAccountsQuery, walletAddress]);

    const queryDerivedError = useMemo(() => normalizeQueryError(queryError), [queryError]);

    const error = useMemo(() => {
        if (!walletAddress) {
            return hasProvidedWalletAddress ? providedPublicKeyError ?? null : null;
        }

        return queryDerivedError;
    }, [hasProvidedWalletAddress, providedPublicKeyError, queryDerivedError, walletAddress]);

    const tokens = walletAddress ? tokenAccounts : undefined;
    const isLoading = Boolean(walletAddress) && (isInitialLoading || isFetching);

    const contextValue = useMemo<UserTokensState>(
        () => ({
            tokens,
            isLoading,
            refresh,
            walletAddress,
            error
        }),
        [error, isLoading, refresh, tokens, walletAddress]
    );

    return <UserTokensContext.Provider value={contextValue}>{children}</UserTokensContext.Provider>;
};

export const useUserTokens = (): UserTokensState => {
    const context = useContext(UserTokensContext);
    if (!context) {
        throw new Error("useUserTokens must be used within a UserTokensProvider");
    }

    return context;
};
